import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Icon, Menu, MenuItem, Modal } from "semantic-ui-react";

import { DBBCarSection } from "components/DBB/components/DBBCreateContractModal/components/DBBCarSection";
import "components/DBB/components/DBBCreateContractModal/components/DBBCreateContractForm/DBBCreateContractForm.scss";
import {
  DBBCustomerSection,
  DBB_MANDATORY_CUSTOMER_FIELDS,
  hasContractCustomerName
} from "components/DBB/components/DBBCreateContractModal/components/DBBCustomerSection";
import { DBBTyreSetsSection } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSetsSection";
import { useDBBContractOptions } from "components/DBB/components/DBBCreateContractModal/hooks";
import { createFormHelper } from "lib/Form";

import type { DBBContractCar } from "components/DBB/components/DBBCreateContractModal/components/DBBCarSection";
import type { DBBContractCustomer } from "components/DBB/components/DBBCreateContractModal/components/DBBCustomerSection";
import type { DBBContractTyreSet } from "components/DBB/components/DBBCreateContractModal/components/DBBTyreSetsSection";
import type { TFunction } from "i18next";
import type { Car, DBBLeaseCompanyID, DBBStorageLocationID } from "models";
import type { CarCustomerInfo } from "modules/CarDetails/hooks";

export enum DBB_CONTRACT_TYPES {
  Private = "PRIVATE",
  Lease = "LEASE",
  Fleet = "FLEET"
}

export type DBBContractFormPayload = {
  contract_type: DBB_CONTRACT_TYPES;
  pickup_by_dbb: boolean;
  lease_company_id: DBBLeaseCompanyID | null;
  storage_location_id: DBBStorageLocationID | null;
  driver?: DBBContractCustomer;
  owner?: DBBContractCustomer;
  is_driver_unknown?: boolean;
  is_owner_unknown?: boolean;
  car: DBBContractCar;
  tyre_sets: DBBContractTyreSet[];
};

export type DBBContractSection = {
  customerField: "driver" | "owner";
  unknownField: "is_driver_unknown" | "is_owner_unknown";
  label: string;
  defaultText: string;
};

type DBBCreateContractFormProps = {
  car: Car;
  customers?: CarCustomerInfo;
};

const FormHelper = createFormHelper<DBBContractFormPayload>();

const CONTRACT_SECTIONS: Record<DBBContractSection["customerField"], DBBContractSection> = {
  driver: { customerField: "driver", unknownField: "is_driver_unknown", label: "v8_driver", defaultText: "Driver" },
  owner: { customerField: "owner", unknownField: "is_owner_unknown", label: "v8_owner", defaultText: "Owner" }
};

export const getContractSections = (contractType: DBB_CONTRACT_TYPES) => {
  if (contractType === DBB_CONTRACT_TYPES.Lease) return [CONTRACT_SECTIONS.driver];
  if (contractType === DBB_CONTRACT_TYPES.Fleet) return [CONTRACT_SECTIONS.owner, CONTRACT_SECTIONS.driver];

  return [CONTRACT_SECTIONS.owner];
};

export const getIncompleteCustomerSections = (values: DBBContractFormPayload) =>
  getContractSections(values.contract_type).reduce<DBBContractSection["customerField"][]>((customerFields, { customerField, unknownField }) => {
    if (values[unknownField]) return customerFields;

    const customer = values[customerField];
    const hasMissingField = DBB_MANDATORY_CUSTOMER_FIELDS.some(field => !customer?.[field]) || !hasContractCustomerName(customer);

    if (hasMissingField) customerFields.push(customerField);

    return customerFields;
  }, []);

const getContractTypeOptions = (t: TFunction) => [
  { key: DBB_CONTRACT_TYPES.Private, value: DBB_CONTRACT_TYPES.Private, text: t("v8_private", "Private") },
  { key: DBB_CONTRACT_TYPES.Lease, value: DBB_CONTRACT_TYPES.Lease, text: t("v8_lease", "Lease") },
  { key: DBB_CONTRACT_TYPES.Fleet, value: DBB_CONTRACT_TYPES.Fleet, text: t("v8_fleet", "Fleet") }
];

export const DBBCreateContractForm = ({ car, customers }: DBBCreateContractFormProps) => {
  const { t } = useTranslation();
  const { watch, formState } = FormHelper.useFormContext();

  const [activeCustomerField, setActiveCustomerField] = useState<DBBContractSection["customerField"]>("owner");

  const { leaseCompanyOptions, isFetchingLeaseCompanies } = useDBBContractOptions(car);

  const values = watch();
  const isLeaseContract = values.contract_type === DBB_CONTRACT_TYPES.Lease;
  const sections = getContractSections(values.contract_type);
  const activeSection = sections.find(section => section.customerField === activeCustomerField) ?? sections[0];
  const incompleteCustomerFields = formState.isSubmitted ? getIncompleteCustomerSections(values) : [];

  return (
    <>
      <Modal.Content className="modal-content">
        <div className="section contract-type-section">
          <label className="big contract-type-label">
            <Icon className="file contract" />
            {t("v8_contract", "Contract")}
          </label>
          <Form.Group>
            <Form.Field width={6}>
              <label>{t("v8_contract_type", "Contract type")}</label>
              <FormHelper.Field.Dropdown name="contract_type" options={getContractTypeOptions(t)} dropdownProps={{ selection: true, selectOnBlur: false }} />
            </Form.Field>
            {isLeaseContract && (
              <Form.Field width={6} required error={formState.isSubmitted && !values.lease_company_id}>
                <label>{t("v8_lease_company", "Lease company")}</label>
                <FormHelper.Field.Dropdown
                  name="lease_company_id"
                  options={leaseCompanyOptions}
                  dropdownProps={{
                    selection: true,
                    search: true,
                    selectOnBlur: false,
                    loading: isFetchingLeaseCompanies,
                    placeholder: t("v8_lease_company", "Lease company")
                  }}
                />
              </Form.Field>
            )}
          </Form.Group>

          {/* Hidden for now, but we still need to send false in the payload (CHECK-858)
          <Form.Group>
            <FormHelper.Field.Checkbox name="pickup_by_dbb" label={t("v8_pickup_by_dbb", "Pickup by DBB")} formFieldProps={{ width: 6 }} />
          </Form.Group> */}
        </div>

        <div className="contract-form-customers-section">
          <label className="big customer-section-label">
            <Icon className="user" />
            {t("v8_customer", "Customer")}
          </label>
          <Menu tabular className="customer-tabs">
            {sections.map(section => {
              const hasError = incompleteCustomerFields.includes(section.customerField);

              return (
                <MenuItem
                  key={section.customerField}
                  active={section.customerField === activeSection.customerField}
                  className={hasError ? "has-error" : undefined}
                  onClick={() => setActiveCustomerField(section.customerField)}
                >
                  {t(section.label, section.defaultText)}
                  {hasError && <Icon className="exclamation circle" color="red" />}
                </MenuItem>
              );
            })}
          </Menu>

          <DBBCustomerSection key={activeSection.customerField} section={activeSection} customers={customers} hasValidationError={formState.isSubmitted} />
        </div>
        <DBBCarSection />
        <DBBTyreSetsSection car={car} />
      </Modal.Content>
    </>
  );
};
